import React, { memo, useMemo } from "react";
import styled from "@emotion/styled";
import GraphNotice, { GraphNoticeType } from "./GraphNotice";
import GraphLoading from "./GraphLoading";

/**
 * Reserves the viz footprint while the chart subtree (or async data like
 * `world.json`) is hydrating. Without this, GraphLoading is absolutely
 * positioned with no flow content, so the parent grid cell collapses and the
 * chart pushes content down once it mounts — visible as a large CLS jump on
 * first paint.
 */
const VizPlaceholder = styled.div`
  position: relative;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 360px;
`;

interface VisualizationContentProps {
  hasError: boolean;
  hasNoData: boolean;
  /** Records exist but total trade value is 0. */
  hasZeroTrade?: boolean;
  loading: boolean;
  noticeType?: GraphNoticeType;
  /** Error-specific notice type (e.g. RateLimit). Takes precedence over
   *  noticeType when hasError is true. */
  errorNoticeType?: GraphNoticeType;
  children: React.ReactNode;
}

const VisualizationContent = memo(
  ({
    hasError,
    hasNoData,
    hasZeroTrade,
    loading,
    noticeType,
    errorNoticeType,
    children,
  }: VisualizationContentProps) => {
    const content = useMemo(() => {
      if (hasError) {
        return (
          <GraphNotice
            graphNoticeType={
              errorNoticeType ?? noticeType ?? GraphNoticeType.Error
            }
          />
        );
      }
      if (hasNoData) {
        return (
          <GraphNotice
            graphNoticeType={noticeType ?? GraphNoticeType.NoData}
          />
        );
      }
      if (hasZeroTrade) {
        return (
          <GraphNotice
            graphNoticeType={GraphNoticeType.ZeroTrade}
          />
        );
      }
      return children;
    }, [hasError, hasNoData, hasZeroTrade, noticeType, errorNoticeType, children]);

    return (
      <VizPlaceholder>
        {content}
        {loading && !hasError && <GraphLoading />}
      </VizPlaceholder>
    );
  },
);

VisualizationContent.displayName = "VisualizationContent";

export default VisualizationContent;
